import {
  ApplicationRef,
  ComponentRef,
  Directive,
  ElementRef,
  EmbeddedViewRef,
  HostListener,
  Injector,
  Input,
  OnDestroy,
  ViewContainerRef,
} from '@angular/core';

import { SedsPositionsOptions } from '../../../shared/enums/seds-positions-options.enum';
import { SedsPositionsOptionsType } from '../../../shared/types/seds-positions-options.type';
import { SedsTooltipComponent } from '../seds-tooltip.component';

@Directive({
    selector: '[sedsTooltip]',
    standalone: false
})
export class SedsTooltipDirective implements OnDestroy {
  @Input() hideDelay?: number;
  @Input() position: SedsPositionsOptions | SedsPositionsOptionsType | string = SedsPositionsOptions.BELOW;
  @Input() sedsTooltip = '';
  @Input() showDelay?: number;

  private componentRef: ComponentRef<any> | null = null;
  private hideTimeout?: number;
  private showTimeout?: number;

  constructor(
    private readonly appRef: ApplicationRef,
    private readonly elementRef: ElementRef,
    private readonly injector: Injector,
    private readonly viewContainerRef: ViewContainerRef
  ) {}

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.initializeTooltip();
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this.setHideTooltipTimeout();
  }

  @HostListener('focusin')
  onFocus(): void {
    this.initializeTooltip();
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.setHideTooltipTimeout();
  }

  private initializeTooltip() {
    if (this.componentRef === null) {
      window.clearInterval(this.hideTimeout);
      this.componentRef = this.viewContainerRef.createComponent(SedsTooltipComponent, { injector: this.injector });

      const [tooltipDOMElement] = (this.componentRef.hostView as EmbeddedViewRef<any>).rootNodes;

      this.setTooltipComponentProperties();
      document.body.appendChild(tooltipDOMElement);
      this.showTimeout = window.setTimeout(this.showTooltip.bind(this), this.showDelay);
    }
  }

  private setTooltipComponentProperties() {
    if (this.componentRef !== null) {
      this.componentRef.instance.sedsTooltip = this.sedsTooltip;
      this.componentRef.instance.position = this.position;

      const { left, right, top, bottom } = this.elementRef.nativeElement.getBoundingClientRect();
      const middlePosition = 2;
      switch (this.position) {
        case SedsPositionsOptions.BELOW:
        case SedsPositionsOptions.BOTTOM: {
          this.componentRef.instance.left = Math.round((right - left) / middlePosition + left);
          this.componentRef.instance.top = Math.round(bottom);
          break;
        }
        case SedsPositionsOptions.ABOVE:
        case SedsPositionsOptions.TOP: {
          this.componentRef.instance.left = Math.round((right - left) / middlePosition + left);
          this.componentRef.instance.top = Math.round(top);
          break;
        }
        case SedsPositionsOptions.RIGHT: {
          this.componentRef.instance.left = Math.round(right);
          this.componentRef.instance.top = Math.round(top + (bottom - top) / middlePosition);
          break;
        }
        case SedsPositionsOptions.LEFT: {
          this.componentRef.instance.left = Math.round(left);
          this.componentRef.instance.top = Math.round(top + (bottom - top) / middlePosition);
          break;
        }
        default: {
          break;
        }
      }
    }
  }

  private setHideTooltipTimeout() {
    this.hideTimeout = window.setTimeout(this.ngOnDestroy.bind(this), this.hideDelay);
  }

  private showTooltip() {
    if (this.componentRef !== null) {
      this.componentRef.instance.visible = true;
    }
  }

  ngOnDestroy(): void {
    if (this.componentRef !== null) {
      window.clearInterval(this.showTimeout);
      window.clearInterval(this.hideTimeout);
      this.appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = null;
    }
  }
}
